// Klaviyo Back in Stock Custom Element
class KlaviyoBackInStock extends HTMLElement {
  constructor() {
    super();
    this.modalRoot = null;
    this.triggers = null;
    this.closeBtn = null;
    this.form = null;
    this.originalFormHTML = null;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    // Find elements
    this.modalRoot = document.getElementById("bis-modal-root");
    if (!this.modalRoot) return; // Exit if element doesn't exist (product is in stock)

    this.triggers = document.querySelectorAll(".bis-trigger");
    this.closeBtn = this.querySelector(".c-modal__close");
    this.form = this.querySelector(".back-in-stock-form");

    this.product = JSON.parse(
      document.querySelector(
        "script[type='application/json'].bold-product-json"
      )?.textContent
    );
    // Store the original form HTML when initializing
    this.originalFormHTML =
      this.modalRoot.querySelector(".c-modal__body").innerHTML;

    // Bind event handlers
    this.bindEvents();
    
    // Add direct event listener to document for all bis-trigger clicks
    this.addGlobalClickHandler();
  }
  
  addGlobalClickHandler() {
    // Direct document-level click handler for all .bis-trigger elements
    document.addEventListener('click', (event) => {
      // Check if we're clicking on a bis-trigger element or its child
      const bisTrigger = event.target.closest('.bis-trigger');
      
      if (bisTrigger) {
        event.preventDefault();
        event.stopPropagation();
        
        // Open the modal
        this.openModal({
          variantId: bisTrigger.dataset.variantId,
          productId: bisTrigger.dataset.productId,
          title: bisTrigger.dataset.variantTitle,
        });
      }
    }, true); // Use capture phase to ensure we get the event first
    
    // Also add specific handler for the drawer triggers
    const variantPopup = document.getElementById('variant-popup');
    if (variantPopup) {
      
      // Watch for changes to the variant popup
      const observer = new MutationObserver((mutations) => {
        // For each mutation, check if we need to add event handlers
        mutations.forEach(mutation => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
            // Check if the variant popup is now visible
            if (variantPopup.style.display !== 'none') {
              
              // Find all bis-triggers in the variant popup
              const drawerTriggers = variantPopup.querySelectorAll('.bis-trigger');
              if (drawerTriggers.length > 0) {
                
                // Add a special class to make them even more identifiable
                drawerTriggers.forEach(trigger => {
                  trigger.classList.add('bis-trigger-drawer');
                  trigger.style.cursor = 'pointer';
                  trigger.setAttribute('data-bis-attached', 'true');
                });
              } else {
              }
            }
          }
        });
      });
      
      // Observe the variant popup for style changes (which indicate visibility changes)
      observer.observe(variantPopup, { 
        attributes: true,
        attributeFilter: ['style']
      });
    }
  }

  bindEvents() {
    // Open modal for initial triggers found on page load
    this.triggers.forEach((trigger) => {
      
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        
        this.openModal({
          variantId: trigger.dataset.variantId,
          productId: trigger.dataset.productId,
          title: trigger.dataset.variantTitle,
        });
      });
    });

    // Enhanced modal closing - multiple methods
    // 1. Close button click with improved error handling
    if (this.closeBtn) {
      this.closeBtn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.closeModal();
      });

      // Add a data attribute for additional targeting
      this.closeBtn.setAttribute('data-modal-close', 'true');
    }

    // 2. Close when clicking outside with improved targeting
    this.modalRoot.addEventListener("click", (e) => {
      // Close if clicking directly on the modal background
      if (e.target === this.modalRoot) {
        this.closeModal();
      }
      
      // Also close if clicking on an element with data-modal-close attribute
      if (e.target.closest('[data-modal-close="true"]')) {
        e.preventDefault();
        e.stopPropagation();
        this.closeModal();
      }
    });

    // 3. Close on Escape key press
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isModalOpen()) {
        this.closeModal();
      }
    });

    // Handle form submission - binding 'this' context explicitly
    this.form.addEventListener("submit", this.handleSubmit.bind(this));
  }

  // Check if the modal is currently open
  isModalOpen() {
    return this.modalRoot && 
           (this.modalRoot.style.display === 'flex' || 
            this.modalRoot.style.display === 'block' ||
            this.modalRoot.classList.contains('is-open') ||
            this.modalRoot.classList.contains('show'));
  }

  openModal({ variantId, productId, title }) {
    this.modalRoot.querySelector(".c-modal__subtitle").textContent = title;
    this.form.setAttribute("data-variant-id", variantId);
    this.modalRoot.style.display = "flex";
    document.body.style.overflow = "hidden";
    document.body.classList.add("modal-open");
    
    // Ensure all close buttons in the modal have the data attribute
    this.modalRoot.querySelectorAll('.c-modal__close, .modal-close, [aria-label="Close"]')
      .forEach(btn => btn.setAttribute('data-modal-close', 'true'));
  }

  closeModal() {
    try {
      // Primary close method
      this.modalRoot.style.display = "none";
      
      // Secondary close methods (for different modal implementations)
      this.modalRoot.classList.remove('is-open', 'is-active', 'is-visible', 'shown', 'show');
      
      // If it's a <dialog> element, try the close method
      if (typeof this.modalRoot.close === 'function') {
        this.modalRoot.close();
      }
      
      // Restore body state
      document.body.style.overflow = "";
      document.body.classList.remove("modal-open");
    } catch (error) {
      console.error('Error closing modal:', error);
      
      // Fallback close method - try to find the modal by ID if this.modalRoot is not available
      const modalElement = document.getElementById("bis-modal-root");
      if (modalElement) {
        modalElement.style.display = "none";
        document.body.style.overflow = "";
        document.body.classList.remove("modal-open");
      }
    }
  }

  handleSubmit(e) {
    e.preventDefault();

    const variantId = this.form.getAttribute("data-variant-id");
    const email = this.modalRoot.querySelector("#bis-email").value;

    // Collect all form data
    const formData = {
      email: email,
      fullName: this.modalRoot.querySelector("#bis-fullname").value,
      phoneNumber: this.modalRoot.querySelector("#bis-phone").value,
      postCode: this.modalRoot.querySelector("#bis-postcode").value,
      m2Required: this.modalRoot.querySelector("#bis-m2").value,
      timeframe: this.modalRoot.querySelector("#bis-timeframe").value,
      notes: this.modalRoot.querySelector("#bis-notes").value || "",
      marketingConsent: this.modalRoot.querySelector("#bis-marketing-consent")
        .checked,
    };

    // Use Klaviyo's embedded back in stock form
    this.submitToKlaviyoEmbed(variantId, formData);
  }

  submitToKlaviyoEmbed(variantId, formData) {
    // Show loading state
    const submitBtn = this.form.querySelector('button[type="submit"]');
    const originalBtnText = submitBtn.textContent;
    submitBtn.textContent = "Submitting...";
    submitBtn.disabled = true;

    // Prepare data for the Klaviyo API - using the exact format from the example
    const klaviyoData = {
      data: {
        type: "back-in-stock-subscription",
        attributes: {
          profile: {
            data: {
              type: "profile",
              attributes: {
                email: formData.email,
              },
            },
          },
          channels: ["EMAIL"],
        },
        relationships: {
          variant: {
            data: {
              type: "catalog-variant",
              id: `$shopify:::$default:::${variantId}`,
            },
          },
        },
      },
    };

    // Make the API request
    fetch(
      "https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=VCRePF",
      {
        method: "POST",
        headers: {
          accept: "application/vnd.api+json",
          "content-type": "application/vnd.api+json",
          revision: "2025-04-15",
        },
        body: JSON.stringify(klaviyoData),
      }
    )
      .then((data) => {
        // Show success message
        const modalBody = this.modalRoot.querySelector(".c-modal__body");
        modalBody.innerHTML =
          '<div class="success-message">Thanks! We\'ll notify you when this item is back in stock.</div>';

        // Also identify the user with additional properties
        this.identifyUser({
          $email: formData.email,
          $full_name: formData.fullName,
          $phone_number: formData.phoneNumber,
          $postal_code: formData.postCode,
          $m2_required: formData.m2Required,
          $required_timeframe: formData.timeframe,
          $notes: formData.notes,
          $source: "Back in Stock Form",
          $accepts_marketing: formData.marketingConsent, // Make sure this is set correctly
        });

        // Close the modal after a delay
        setTimeout(() => this.closeModal(), 3000);
      })
      .catch((error) => {
        console.error("Error submitting back in stock request:", error);

        // Show error message
        const modalBody = this.modalRoot.querySelector(".c-modal__body");
        modalBody.innerHTML =
          '<div class="error-message">There was an error submitting your request. Please try again.</div>';

        // Add a retry button
        const retryBtn = document.createElement("button");
        retryBtn.className =
          "c-button c-button--full c-button--primary retry-button";
        retryBtn.textContent = "Try Again";
        retryBtn.addEventListener("click", () => {
          // Restore the original form
          modalBody.innerHTML = this.originalFormHTML || "";

          // Re-initialize form event listeners
          this.form = this.modalRoot.querySelector(".back-in-stock-form");
          this.form.addEventListener("submit", this.handleSubmit.bind(this));
        });

        modalBody.querySelector(".error-message").appendChild(retryBtn);
      })
      .finally(() => {
        // Reset button state
        submitBtn.textContent = originalBtnText;
        submitBtn.disabled = false;
      });

    // If marketing consent was given, also subscribe to the list
    if (formData.marketingConsent) {
      try {
        // Subscribe to the main marketing list
        const listId = "WfygPF";
        const body = {
          data: {
            type: "subscription",
            attributes: {
              profile: {
                data: {
                  type: "profile",
                  attributes: {
                    email: formData.email,
                  },
                },
              },
            },
            relationships: {
              list: {
                data: {
                  type: "list",
                  id: listId,
                },
              },
            },
          },
        };

        fetch("https://a.klaviyo.com/client/subscriptions?company_id=VCRePF", {
          method: "POST",
          headers: {
            accept: "application/vnd.api+json",
            revision: "2025-04-15",
            "content-type": "application/vnd.api+json",
          },
          body: JSON.stringify(body),
        }).catch((error) => {
          console.error("Error subscribing to list:", error);
        });
      } catch (error) {
        console.error("Error with Klaviyo JS API:", error);
      }
    }
  }

  identifyUser(properties) {
    // Using Klaviyo's client-side identify API
    if (window._learnq && Array.isArray(window._learnq)) {
      window._learnq.push(["identify", properties]);
    }

    // Fallback to the server-side API if _learnq is not available
    const identifyData = {
      token: "VCRePF", // Your public API key
      properties: properties,
    };

    return fetch("https://a.klaviyo.com/api/identify", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(identifyData),
    });
  }
}

// Register the custom element
if (!customElements.get("klaviyo-back-in-stock")) {
  customElements.define("klaviyo-back-in-stock", KlaviyoBackInStock);
}

// STANDALONE SCRIPT FOR BIS-TRIGGER BUTTONS
// This is completely independent of the above custom element
document.addEventListener('DOMContentLoaded', function() {
  console.warn('🔍 STANDALONE BIS-TRIGGER HANDLER: Initializing...');
  
  // Function to find the modal
  function findBisModal() {
    // First, try the expected ID
    const modal = document.getElementById("bis-modal-root");
    if (modal) return modal;
    
    // If not found, let's scan the DOM for the real modal
    scanForBisModals();
    
    // Return null to indicate we couldn't find it directly
    return null;
  }
  
  // Function to deeply scan the DOM for any back-in-stock related modals
  function scanForBisModals() {
    console.warn('🔍 SCANNING DOM: Looking for any back-in-stock related modals...');
    
    // Check for various possible modal selectors
    const possibleSelectors = [
      // ID-based selectors
      '#bis-modal-root',
      '#bis-modal',
      '#back-in-stock-modal',
      '#klaviyo-bis-modal',
      '[id*="bis"]',
      '[id*="back-in-stock"]',
      '[id*="klaviyo"]',
      
      // Class-based selectors
      '.klaviyo-bis-modal',
      '.back-in-stock-modal',
      '.bis-modal',
      '[class*="bis"]',
      '[class*="back-in-stock"]',
      
      // General modal classes that might contain BIS functionality
      '.c-modal',
      '.modal.in',
      '.modal--is-active',
      
      // Attribute-based
      '[data-modal-type="bis"]',
      '[data-section-type="bis"]',
      '[aria-labelledby*="bis"]',
      
      // Some known third-party selectors
      '#SI_trigger',
      '#SI_frame',
      '.si-content',
      '#back-in-stock_form',
      '.gBackInStock'
    ];
    
    // Results will store all found elements
    const results = {};
    
    // Check each selector
    possibleSelectors.forEach(selector => {
      try {
        const elements = document.querySelectorAll(selector);
        if (elements.length > 0) {
          results[selector] = Array.from(elements).map(el => ({
            element: el,
            id: el.id,
            classList: Array.from(el.classList),
            tagName: el.tagName,
            visibility: {
              display: window.getComputedStyle(el).display,
              visibility: window.getComputedStyle(el).visibility,
              hidden: el.hidden,
              ariaHidden: el.getAttribute('aria-hidden')
            },
            html: el.outerHTML.substring(0, 200) + (el.outerHTML.length > 200 ? '...' : '')
          }));
          
          // Enhance any modals we find with robust closing functionality
          elements.forEach(enhanceModalCloseButtons);
        }
      } catch (e) {
        console.error(`Error checking selector ${selector}:`, e);
      }
    });
    
    // Also check for the components by direct selector
    const customElement = document.querySelector('klaviyo-back-in-stock');
    if (customElement) {
      results['custom-element'] = {
        element: customElement,
        modalRoot: customElement.modalRoot,
        html: customElement.outerHTML.substring(0, 200) + '...'
      };
    }
    
    // Log all found elements
    console.warn('🔍 SCAN RESULTS:', results);
    
    // Look for elements that explicitly mention "klaviyo" and "back in stock" in their HTML
    const allElements = document.querySelectorAll('*');
    const relevantElements = Array.from(allElements).filter(el => {
      const html = el.outerHTML.toLowerCase();
      return (html.includes('klaviyo') && html.includes('back') && html.includes('stock')) ||
             (html.includes('bis') && html.includes('modal')) ||
             (html.includes('notify') && html.includes('available'));
    });
    
    if (relevantElements.length > 0) {
      console.warn('🔍 Found elements containing klaviyo/back-in-stock text:', relevantElements);
      console.warn('Details of first relevant element:', {
        element: relevantElements[0],
        html: relevantElements[0].outerHTML.substring(0, 200) + '...'
      });
      
      // Enhance these relevant elements too
      relevantElements.forEach(enhanceModalCloseButtons);
    }
    
    return results;
  }
  
  // New function to enhance modal close buttons
  function enhanceModalCloseButtons(modalElement) {
    if (!modalElement) return;
    
    try {
      // 1. Find all potential close buttons in the modal
      const closeButtons = modalElement.querySelectorAll('.c-modal__close, .modal-close, .close, [aria-label="Close"], button[type="reset"]');
      
      // 2. Add robust close functionality to each button
      closeButtons.forEach(btn => {
        // Add data attribute for targeting
        btn.setAttribute('data-modal-close', 'true');
        
        // Add click listener that uses multiple close methods
        btn.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();
          closeModalRobustly(modalElement);
        });
      });
      
      // 3. Add click outside functionality
      modalElement.addEventListener('click', function(e) {
        // Only close if clicking directly on the modal background (not its children)
        if (e.target === modalElement) {
          closeModalRobustly(modalElement);
        }
        
        // Also close if clicking on any element with the close data attribute
        if (e.target.closest('[data-modal-close="true"]')) {
          e.preventDefault();
          e.stopPropagation();
          closeModalRobustly(modalElement);
        }
      });
      
      // 4. If no close buttons found, try to add one
      if (closeButtons.length === 0) {
        // Find a reasonable container for the close button
        const modalHeader = modalElement.querySelector('.modal-header, .c-modal__header, header, .header');
        
        if (modalHeader) {
          const newCloseBtn = document.createElement('button');
          newCloseBtn.type = 'button';
          newCloseBtn.className = 'c-modal__close modal-close close';
          newCloseBtn.setAttribute('data-modal-close', 'true');
          newCloseBtn.setAttribute('aria-label', 'Close');
          newCloseBtn.innerHTML = '&times;'; // × symbol
          newCloseBtn.style.cssText = 'position:absolute;right:10px;top:10px;font-size:24px;cursor:pointer;z-index:9999;';
          
          newCloseBtn.addEventListener('click', function(e) {
            e.preventDefault();
            e.stopPropagation();
            closeModalRobustly(modalElement);
          });
          
          modalHeader.appendChild(newCloseBtn);
        }
      }
    } catch (error) {
      console.error('Error enhancing modal close buttons:', error);
    }
  }
  
  // New robust function to close modals with multiple methods
  function closeModalRobustly(modalElement) {
    if (!modalElement) return;
    
    try {
      // Try multiple methods to hide the modal
      
      // Method 1: Set display style
      modalElement.style.display = 'none';
      
      // Method 2: Remove common "shown" classes
      modalElement.classList.remove('is-open', 'is-active', 'is-visible', 'shown', 'show', 'in', 'open');
      
      // Method 3: Add hidden classes
      modalElement.classList.add('hidden', 'is-hidden');
      
      // Method 4: Set aria-hidden attribute
      modalElement.setAttribute('aria-hidden', 'true');
      
      // Method 5: Try the close() method for <dialog> elements
      if (typeof modalElement.close === 'function') {
        modalElement.close();
      }
      
      // Method 6: If has a close button that might have special code, try clicking it
      if (!modalElement.querySelector('[data-modal-close="true"]')) {
        const closeBtn = modalElement.querySelector('.c-modal__close:not([data-modal-close="true"]), .modal-close:not([data-modal-close="true"]), .close:not([data-modal-close="true"])');
        if (closeBtn) {
          closeBtn.click();
        }
      }
      
      // Restore body state
      document.body.style.overflow = '';
      document.body.classList.remove('modal-open');
      
      console.warn('🔍 Modal closed successfully');
    } catch (error) {
      console.error('Error closing modal:', error);
      
      // Super fallback: try to find and close all potential modals
      try {
        document.querySelectorAll('.c-modal, .modal, [role="dialog"]').forEach(modal => {
          modal.style.display = 'none';
        });
        document.body.style.overflow = '';
        document.body.classList.remove('modal-open');
      } catch (e) {
        console.error('Final fallback error:', e);
      }
    }
  }
  
  // Function to log info about all the bis-triggers on the page
  function logAllBisTriggers() {
    console.warn('🔍 STANDALONE: Scanning all bis-triggers on page...');
    const allTriggers = document.querySelectorAll('.bis-trigger');
    console.warn(`🔍 STANDALONE: Found ${allTriggers.length} bis-triggers on page`);
    
    allTriggers.forEach((trigger, index) => {
      console.warn(`🔍 STANDALONE: Trigger #${index + 1}:`, {
        element: trigger,
        html: trigger.outerHTML,
        variantId: trigger.dataset.variantId,
        productId: trigger.dataset.productId,
        variantTitle: trigger.dataset.variantTitle,
        isVisible: isElementVisible(trigger),
        clickable: isElementClickable(trigger)
      });
    });
  }
  
  // Function to check if an element is visible
  function isElementVisible(element) {
    if (!element) return false;
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
  }
  
  // Function to check if an element should be clickable
  function isElementClickable(element) {
    if (!element) return false;
    const style = window.getComputedStyle(element);
    return style.pointerEvents !== 'none';
  }
  
  // Function to set up a variant drawer observer
  function setupVariantDrawerObserver() {
    const variantDrawer = document.getElementById('variant-popup');
    if (!variantDrawer) {
      console.warn('❌ STANDALONE: Variant drawer not found on page');
      return;
    }
    
    console.warn('✅ STANDALONE: Variant drawer found, setting up observer');
    
    // Function to handle variant drawer opening
    function handleDrawerVisibilityChange() {
      if (variantDrawer.style.display !== 'none') {
        console.warn('🔍 STANDALONE: Variant drawer opened!');
        // Wait a moment for any animations to finish and content to fully load
        setTimeout(() => {
          const drawerTriggers = variantDrawer.querySelectorAll('.bis-trigger');
          console.warn(`🔍 STANDALONE: Found ${drawerTriggers.length} triggers in drawer`);
          
          // Enhance each trigger for better visibility and clickability
          drawerTriggers.forEach((trigger, index) => {
            
            // Add a unique ID we can refer to later
            trigger.id = `standalone-bis-trigger-${index}`;
            
            // Add a direct click handler to this specific element
            trigger.addEventListener('click', function(e) {
              e.preventDefault();
              e.stopPropagation();
              
              // Open the modal directly
              openBisModal(trigger);
            });
          });
        }, 500); // Small delay to ensure content is fully loaded
      }
    }
    
    // Set up a MutationObserver to detect when the drawer opens
    const observer = new MutationObserver((mutations) => {
      mutations.forEach(mutation => {
        if (mutation.attributeName === 'style') {
          handleDrawerVisibilityChange();
        }
      });
    });
    
    observer.observe(variantDrawer, {
      attributes: true,
      attributeFilter: ['style']
    });
    
    // Also check immediately in case it's already open
    handleDrawerVisibilityChange();
  }
  
  // Function to open the BIS modal with the given trigger data
  function openBisModal(trigger) {
    // Check if modal element exists
    const modal = findBisModal();
    
    if (!modal) {
      console.error('STANDALONE: Cannot open modal - bis-modal-root not found!');
      
      // Check if any modal-like elements exist
      console.warn('Searching for possible modal containers...');
      const possibleModals = [
        document.getElementById('bis-modal-root'),
        document.querySelector('.klaviyo-back-in-stock'),
        document.querySelector('[id*="bis"]'),
        document.querySelector('[id*="klaviyo"]'),
        document.querySelector('[class*="bis"]'),
        document.querySelector('[class*="klaviyo"]'),
        document.querySelector('.c-modal'),
        ...Array.from(document.querySelectorAll('[id*="modal"]')),
        ...Array.from(document.querySelectorAll('[class*="modal"]'))
      ];
      
      // Filter out null values and log possible modals
      const validModals = possibleModals.filter(Boolean);
      console.warn('Possible modal elements found:', validModals);
      
      // Try to use the first valid modal if any exist
      if (validModals.length > 0) {
        console.warn('Attempting to use first found modal as fallback:', validModals[0]);
        
        // Enhance the modal with close buttons before opening it
        enhanceModalCloseButtons(validModals[0]);
        
        // Try to open this modal instead
        openModalWithFallbacks(validModals[0], trigger);
        return;
      }
      
      // Let's also check if the klaviyo-back-in-stock custom element exists
      const customElement = document.querySelector('klaviyo-back-in-stock');
      console.warn('Klaviyo custom element found?', !!customElement);
      
      return;
    }
    
    // Extract data from trigger
    const variantId = trigger.dataset.variantId;
    const productId = trigger.dataset.productId;
    const title = trigger.dataset.variantTitle;
    
    if (!variantId || !title) {
      return;
    }
    
    // Enhance the modal with improved close buttons before opening
    enhanceModalCloseButtons(modal);
    
    // Find required elements inside modal
    const subtitle = modal.querySelector('.c-modal__subtitle');
    const form = modal.querySelector('.back-in-stock-form');
    
    // Set modal title if subtitle element exists
    if (subtitle) {
      subtitle.textContent = title;
    } else {
      console.error('Could not find .c-modal__subtitle element in the modal');
    }
    
    // Set the variant ID on the form if form exists
    if (form) {
      form.setAttribute('data-variant-id', variantId);
    } else {
      console.error('Could not find .back-in-stock-form element in the modal');
    }
    
    // Use robust opener function
    openModalWithFallbacks(modal, trigger);
    
    // Also try invoking the original component's openModal method
    try {
      const klaviyoComponent = document.querySelector('klaviyo-back-in-stock');
      if (klaviyoComponent && typeof klaviyoComponent.openModal === 'function') {
        console.warn('Trying to call original component openModal method...');
        klaviyoComponent.openModal({
          variantId: variantId,
          productId: productId,
          title: title
        });
      }
    } catch (error) {
      console.error('❌ Error calling original openModal method:', error);
    }
  }
  
  // New function to open modals with multiple fallback methods
  function openModalWithFallbacks(modal, trigger) {
    if (!modal) return;
    
    try {
      // Add keyboard escape handler for this modal
      const escHandler = function(e) {
        if (e.key === 'Escape') {
          closeModalRobustly(modal);
          // Remove this handler after modal is closed
          document.removeEventListener('keydown', escHandler);
        }
      };
      document.addEventListener('keydown', escHandler);
      
      // Method 1: Set display style directly
      modal.style.display = 'flex';
      document.body.style.overflow = 'hidden';
      document.body.classList.add('modal-open');
      
      // Method 2: Add common "shown" classes
      modal.classList.add('is-open', 'is-active', 'is-visible', 'show');
      
      // Method 3: Remove hidden classes
      modal.classList.remove('hidden', 'is-hidden');
      
      // Method 4: Set aria-hidden attribute
      modal.setAttribute('aria-hidden', 'false');
      
      // Method 5: Try the show() method for <dialog> elements
      if (typeof modal.show === 'function') {
        modal.show();
      }
      
      console.warn('Modal opened successfully with fallback methods');
      
      // Check if display style actually changed after a short delay
      setTimeout(() => {
        const computedStyle = window.getComputedStyle(modal);
        console.warn('Modal display after opening:', computedStyle.display);
        
        if (computedStyle.display === 'none') {
          console.warn('Modal still not visible after primary methods, trying last resorts...');
          
          // Last resort - try to move the modal to body if it's not already there
          if (modal.parentElement !== document.body) {
            // Clone the modal to preserve event listeners
            const modalClone = modal.cloneNode(true);
            document.body.appendChild(modalClone);
            
            // Re-enhance with close buttons
            enhanceModalCloseButtons(modalClone);
            
            // Try display methods again on the clone
            modalClone.style.display = 'flex';
            modalClone.style.position = 'fixed';
            modalClone.style.top = '0';
            modalClone.style.left = '0';
            modalClone.style.width = '100%';
            modalClone.style.height = '100%';
            modalClone.style.zIndex = '9999';
            modalClone.classList.add('is-open', 'is-active', 'is-visible', 'show');
          }
        }
      }, 100);
    } catch (error) {
      console.error('❌ Error trying to display modal:', error);
    }
  }
  
  // Set up global click handler
  document.addEventListener('click', function(event) {
    // Create a path from event target up to document
    let path = event.composedPath ? event.composedPath() : [];
    
    // If path isn't available, build it manually
    if (!path.length) {
      path = [];
      let el = event.target;
      while (el) {
        path.push(el);
        el = el.parentElement;
      }
    }
    
    // Check if any element in the path has the bis-trigger class
    const bisTriggerInPath = path.find(el => 
      el.classList && el.classList.contains('bis-trigger')
    );
    
    if (bisTriggerInPath) {
      event.preventDefault();
      event.stopPropagation();
      
      // Open the modal directly
      openBisModal(bisTriggerInPath);
      return false;
    }
  }, true); // Use capture phase to get the event before other handlers
  
  // Run initial setup after a short delay to ensure the page is fully loaded
  setTimeout(() => {
    logAllBisTriggers();
    setupVariantDrawerObserver();
    
    // Add direct component access attempt
    tryAccessCustomElementDirectly();
    
    // Set up a periodic checker to catch dynamically added triggers
    setInterval(logAllBisTriggers, 5000);
  }, 1000);
  
  // Function to attempt to directly access the klaviyo-back-in-stock custom element
  function tryAccessCustomElementDirectly() {
    // Try to get the custom element
    const customElement = document.querySelector('klaviyo-back-in-stock');
    
    if (!customElement) {
      console.warn('No klaviyo-back-in-stock custom element found on page');
      return;
    }
    
    // Enhance triggers with direct access
    function enhanceTriggerWithDirectAccess(trigger) {
      // Store original click handler
      const originalOnClick = trigger.onclick;
      
      // Replace with our own that directly calls the component
      trigger.onclick = function(event) {
        event.preventDefault();
        event.stopPropagation();
        
        // Get data attributes
        const variantId = trigger.dataset.variantId;
        const productId = trigger.dataset.productId;
        const title = trigger.dataset.variantTitle;
        
        if (!variantId || !title) {
          console.error('Missing required attributes on trigger:', trigger);
          return false;
        }
        
        // Alert for debugging
        alert(`Direct component access: Variant ${title}`);
        
        // Try to directly call the custom element's methods
        try {
          // Method 1: Try to access modalRoot directly
          if (customElement.modalRoot) {
            console.warn('Accessing modalRoot directly:', customElement.modalRoot);
            
            // Set subtitle
            const subtitle = customElement.modalRoot.querySelector('.c-modal__subtitle');
            if (subtitle) subtitle.textContent = title;
            
            // Set form variant ID
            const form = customElement.modalRoot.querySelector('.back-in-stock-form');
            if (form) form.setAttribute('data-variant-id', variantId);
            
            // Show the modal
            customElement.modalRoot.style.display = 'flex';
            document.body.style.overflow = 'hidden';
            document.body.classList.add('modal-open');
          }
          
          // Method 2: Try to call openModal method
          if (typeof customElement.openModal === 'function') {
            console.warn('Calling openModal method directly');
            customElement.openModal({
              variantId: variantId,
              productId: productId,
              title: title
            });
          }
          
          // If we can't do either, try to create a synthetic click event on the trigger
          // that will be processed by the custom element's event listeners
          if (!customElement.modalRoot && typeof customElement.openModal !== 'function') {
            console.warn('Dispatching synthetic click event to original handler');
            
            // Call original click handler if it exists
            if (typeof originalOnClick === 'function') {
              originalOnClick.call(trigger, event);
            }
            
            // Also try a clean synthetic click
            const syntheticClick = new MouseEvent('click', {
              bubbles: true,
              cancelable: true,
              view: window
            });
            
            // Temporarily remove our handler to avoid infinite loop
            const currentHandler = trigger.onclick;
            trigger.onclick = null;
            
            // Dispatch event
            trigger.dispatchEvent(syntheticClick);
            
            // Restore our handler
            setTimeout(() => {
              trigger.onclick = currentHandler;
            }, 100);
          }
        } catch (error) {
          console.error('Error trying to access custom element:', error);
        }
        
        return false;
      };
    }
    
    // Apply to all bis-triggers
    document.querySelectorAll('.bis-trigger').forEach(enhanceTriggerWithDirectAccess);
    
    // Also set up an observer for future bis-triggers
    const observer = new MutationObserver((mutations) => {
      mutations.forEach(mutation => {
        if (mutation.addedNodes.length) {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check if the node itself is a bis-trigger
              if (node.classList && node.classList.contains('bis-trigger')) {
                enhanceTriggerWithDirectAccess(node);
              }
              
              // Check child elements
              const childTriggers = node.querySelectorAll('.bis-trigger');
              childTriggers.forEach(enhanceTriggerWithDirectAccess);
            }
          });
        }
      });
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
});
